/**
 * `/verify-email` — where the link in the confirmation mail lands.
 *
 * The whole page is one mutation fired on mount and four things it can say. It is a MUTATION and
 * not a query even though the reader did nothing but follow a link, because spending a token is
 * a write: the row is marked consumed and, the first time, a welcome mail goes out. Firing it
 * from an effect is what makes a link a link rather than a page with a button on it.
 *
 * **IT IS FIRED EXACTLY ONCE**, guarded by a ref rather than by an empty dependency array —
 * React runs an effect twice in development's strict mode, and the second run would spend a
 * token the first one had already consumed and turn every successful confirmation into "that
 * link has already been used".
 *
 * There is no session anywhere in this file. The token names the account by itself, so the
 * reader may be on a different device from the one that registered, signed in as somebody else,
 * or signed in as nobody — which is the ordinary case, and the reason `auth.verifyEmail` is
 * public.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ResendVerification } from '@/components/auth/resend-verification';
import { VERIFY_EMAIL_TOKEN_PARAM } from '@chompdome/shared/constants';
import { offersResend, verifyEmailFailureMessage } from '@/lib/verification';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/verify-email')({
  component: VerifyEmailPage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const token = search[VERIFY_EMAIL_TOKEN_PARAM];
    return { token: typeof token === 'string' ? token : undefined };
  },
});

function VerifyEmailPage() {
  const { token } = Route.useSearch();
  const verify = trpc.auth.verifyEmail.useMutation();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !token) return;
    fired.current = true;
    verify.mutate({ token });
  }, [token, verify.mutate]);

  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Confirming your email</CardTitle>
          <CardDescription>
            One click and your account is ready to sign in anywhere.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Body token={token} verify={verify} />
        </CardContent>
      </Card>
    </div>
  );
}

/** The mutation as this page reads it — the four states, and nothing about tRPC's own shape. */
interface VerifyState {
  isPending: boolean;
  error: { message: string } | null;
  data?: { ok: boolean; failure?: string; alreadyVerified?: boolean } | undefined;
}

function Body({ token, verify }: { token: string | undefined; verify: VerifyState }) {
  // No token at all: the link was cut in half by a mail client, or somebody typed the path.
  // Offer the way out rather than an error with nothing behind it.
  if (!token) {
    return (
      <>
        <p className="text-sm text-destructive" role="alert">
          That link is incomplete — some mail apps cut long links in half. Ask for a new one.
        </p>
        <ResendVerification email="" askForEmail />
        <SignInLink />
      </>
    );
  }

  if (verify.isPending || verify.data === undefined) {
    if (verify.error) {
      return (
        <>
          <p className="text-sm text-destructive" role="alert">
            {verify.error.message}
          </p>
          <ResendVerification email="" askForEmail />
          <SignInLink />
        </>
      );
    }
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Checking your link…
      </p>
    );
  }

  if (verify.data.ok) {
    return (
      <>
        <p className="text-sm" role="status">
          {verify.data.alreadyVerified
            ? 'Your email was already confirmed. You are good to go.'
            : 'Your email is confirmed. You can sign in from anywhere now.'}
        </p>
        <Link to="/login">
          <Button className="w-full">Sign in</Button>
        </Link>
      </>
    );
  }

  const failure = verify.data.failure as Parameters<typeof verifyEmailFailureMessage>[0];
  return (
    <>
      <p className="text-sm text-destructive" role="alert">
        {verifyEmailFailureMessage(failure)}
      </p>
      {offersResend(failure) && <ResendVerification email="" askForEmail />}
      <SignInLink />
    </>
  );
}

function SignInLink() {
  return (
    <p className="text-center text-sm text-muted-foreground">
      <Link to="/login" className="font-medium text-primary hover:underline">
        Back to sign in
      </Link>
    </p>
  );
}
